import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

interface ToastValue {
    notify: (message: ReactNode) => void
}

const ToastContext = createContext<ToastValue>({ notify: () => undefined })

export const useToast = (): ToastValue => useContext(ToastContext)

interface ToastProviderProps {
    children: ReactNode
}

export const ToastProvider: FC<ToastProviderProps> = ({ children }): ReactNode => {
    const [toast, setToast] = useState<{ id: number, message: ReactNode } | null>(null)
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const counterRef = useRef(0)

    const notify = useCallback((message: ReactNode) => {
        counterRef.current += 1
        setToast({ id: counterRef.current, message })

        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => setToast(null), 3600)
    }, [])

    useEffect(() => () => clearTimeout(timerRef.current), [])

    const value = useMemo(() => ({ notify }), [notify])

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div
                role='status'
                aria-live='polite'
                className='pointer-events-none fixed inset-x-0 bottom-24 z-[90] flex justify-center px-4 md:bottom-8'
            >
                {toast && (
                    <div
                        key={toast.id}
                        className='pointer-events-auto max-w-md rounded-full bg-ink px-5 py-3 text-center text-sm text-cream shadow-[0_18px_50px_-18px_rgba(0,0,0,0.6)] animate-in fade-in slide-in-from-bottom-3 duration-300'
                    >
                        {toast.message}
                    </div>
                )}
            </div>
        </ToastContext.Provider>
    )
}
